import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import { v4 as uuidv4 } from 'uuid';

import styles from './StreamListItem.module.scss';
import {
  ChevronDown,
  ChevronUp,
  UpToTop,
  DownToBottom,
  TrashCan,
  Locked,
  EventSchedule,
  Policy,
  Edit,
  IncompleteWarning,
  Add,
  Migrate,
  FlowConnection,
  Calendar,
  CameraAction,
} from '@carbon/react/icons';

import { Button, Tooltip } from '@carbon/react';

import { ElementItem } from 'components/Elements';
import useModal from '@/lib/hooks/useModal';
import StreamItemVariantSwitcher from './StreamItemVariantSwitcher';

// pickGroupDefaultVariant — same logic as the dashboard's variant-group
// preview: prefer the explicit default, fall back to the first variant.
const pickGroupDefaultVariant = (group) => {
  if (!group?.variants || group.variants.length === 0) return null;
  if (group.default_variant_id) {
    const v = group.variants.find((x) => x.model.ID === group.default_variant_id);
    if (v) return v;
  }
  return group.variants[0];
};

const StreamList = ({
  elements,
  refreshElements,
  orientation = 0,
  onEditStreamItem,
  invalid = false,
  streamId = 0,
}) => {

  const [showSelectElementModal] = useModal('SELECT_ELEMENT');

  const onMove = (position, index) => {
    let arr = [...elements];
    var element = arr[index];
    arr.splice(index, 1);

    switch (position) {
      case 'up':
        arr.splice(index - 1, 0, element);
        break;
      case 'down':
        arr.splice(index + 1, 0, element);
        break;
      case 'top':
        arr.splice(0, 0, element);
        break;
      case 'bottom':
        arr.splice(elements.length, 0, element);
        break;
      default:
        break;
    }

    refreshElements([...arr]);
  };

  const receiveNewElements = (index, items) => {
    let d = [...elements];

    // Selections from the gallery picker are tile-shaped — they may be
    // single media, pair-aggregated tiles, or synthetic variant-group
    // tiles. We branch per tile so each one gets the right Stream-Item
    // wiring (plain element_id vs. variant_group_id + variant_id).
    const tile_args = (items.selections || []).map((tile) => {
      if (tile._is_variant_group && tile._variant_group) {
        const g = tile._variant_group;
        const defaultVariant = pickGroupDefaultVariant(g);
        // Pick landscape/portrait of the default variant so the editor
        // preview matches what the device of this stream's orientation
        // will actually render at runtime.
        const preferLandscape = orientation !== 1;
        const previewMedia = preferLandscape
          ? (defaultVariant?.landscape_media || defaultVariant?.portrait_media)
          : (defaultVariant?.portrait_media || defaultVariant?.landscape_media);
        return {
          model: { ID: `new_${uuidv4()}` },
          duration: previewMedia?.default_duration || 10,
          element: previewMedia || null,
          element_type: 'element',
          variant_group_id: g.model.ID,
          variant_id: defaultVariant?.model?.ID || null,
          presentation_type: 0,
          _group: g,
        };
      }

      // Pair-aware: in a portrait stream show the portrait half as the
      // visible element, in a landscape (or multi) stream show the
      // landscape half. The element_id stored on the stream item follows
      // the visible half so the device gets the right format on first load.
      let chosenElement = tile;
      if (tile._pair_partner) {
        const wantPortrait = orientation === 1;
        const tileMatches = wantPortrait
          ? tile.orientation === 1
          : tile.orientation !== 1;
        if (!tileMatches) {
          const partner = tile._pair_partner;
          const partnerMatches = wantPortrait
            ? partner.orientation === 1
            : partner.orientation !== 1;
          if (partnerMatches) {
            // Promote partner to primary; keep `tile` as the partner so
            // hover/preview logic still has both halves available.
            chosenElement = {
              ...partner,
              _pair_partner: { ...tile, _pair_partner: null, _is_pair: false },
              _is_pair: true,
            };
          }
        }
      }
      return {
        model: { ID: `new_${uuidv4()}` },
        duration: chosenElement.default_duration,
        element: chosenElement,
        element_type: 'element',
        presentation_type: 0,
      };
    });

    d.splice(index + 1, 0, ...tile_args);

    let stream_items = items.streams || [];
    const stream_args = stream_items.map((i) => ({
      model: { ID: `new_${uuidv4()}` },
      duration: i.default_duration,
      element: i,
      element_type: 'stream',
      presentation_type: 0,
    }));
    d.splice(index + 1, 0, ...stream_args);

    refreshElements([...d]);
  };

  // Replace one item's variant_id with the chosen variant. Updates `element`
  // in lockstep so the preview reflects the selection without waiting for
  // the backend roundtrip — and respects the stream's orientation so a
  // portrait stream surfaces the variant's portrait half (with landscape
  // fallback) and vice versa, even when the variant is itself a Hoch+Quer
  // pair.
  const setVariantId = useCallback((streamItemId, variantId) => {
    const wantPortrait = orientation === 1;
    const next = elements.map((it) => {
      if (it.model.ID !== streamItemId) return it;
      const group = it._group;
      let nextElement = it.element;
      if (group?.variants) {
        const match = group.variants.find((v) => v.model.ID === variantId);
        if (match) {
          nextElement = wantPortrait
            ? (match.portrait_media || match.landscape_media || it.element)
            : (match.landscape_media || match.portrait_media || it.element);
        }
      }
      return { ...it, variant_id: variantId, element: nextElement };
    });
    refreshElements(next);
  }, [elements, refreshElements, orientation]);

  const addElement = (index) => {
    showSelectElementModal({onSelectElements: (items) => {
      receiveNewElements(index, items)
    }, streamId: streamId })
  };

  const removeElement = useCallback((index) => {
    let newState = [...elements];
    newState.splice(index, 1);
    refreshElements([...newState]);
  }, [elements, refreshElements]);

  return (
    <div className={cx(styles.listContainer)}>
      {elements.length > 0 ? (
        <>
          {elements.map((item, index) => {
            console.info("item:", item);
            return (
              <div key={`item_${item.model.ID}`}>
                <div className={cx(styles.itemCard, item.variant_group_id && styles.itemCard_hasVariant)}>
                  <div
                    className={cx(styles.listItem, invalid ? styles.listItem_invalid : null)}>
                    <StreamListItem
                      itemType={item.element?.type}
                      itemURL={item.element?.s3_thumb_url}
                      itemID={item.model.ID}
                      streamItems={item.element_type === "stream" ? item.element?.items : []}
                      isLazy={item.lazy === 1}
                      presentationType={item.presentation_type}
                      hasRule={item.hasRule}
                      onMove={onMove}
                      index={index}
                      onRemove={() => removeElement(index)}
                      orientation={orientation}
                      showDate={item.show_date || false}
                      onEditStreamItem={() => onEditStreamItem(item.model.ID)}
                    />
                    <div className={cx(styles.footer)}>
                      <div className={cx(styles.footerHead)}>
                        {Boolean(item.element?.pair_id) && (
                          <CameraAction
                            size={14}
                            style={{ marginRight: 4, verticalAlign: 'text-bottom' }}
                          />
                        )}
                        {item.element?.name}
                      </div>
                      <div className={cx(styles.footerBody)}>{item.element?.description || item.element?.notes}</div>
                    </div>
                  </div>
                  {item.variant_group_id && (
                    <div className={cx(styles.variantSwitcher)}>
                      <StreamItemVariantSwitcher
                        groupId={item.variant_group_id}
                        inlineGroup={item._group || null}
                        variantId={item.variant_id || null}
                        onChange={(vid) => setVariantId(item.model.ID, vid)}
                      />
                    </div>
                  )}
                </div>

                <div className={cx(styles.addMore)}>
                  <div className={cx(styles.innerButtonField)}>
                    <Button
                      renderIcon={(props) => <Add size={16} {...props}/>}
                      hasIconOnly
                      kind="primary"
                      iconDescription="Neues Element hier einfügen"
                      tooltipPosition="right"
                      tooltipAlignment="center"
                      onClick={() => addElement(index)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className={cx(styles.listItem, styles.listItem_invalid)}>
          <Button
            renderIcon={(props) => <Add size={16} {...props} />}
            hasIconOnly
            kind="primary"
            iconDescription="Neues Element hier einfügen"
            tooltipPosition="right"
            tooltipAlignment="center"
            onClick={() => addElement(0)}
          />
        </div>
      )}
    </div>
  );
};

const StreamListItem = ({
  itemType,
  itemURL,
  _itemID,
  isLazy = false,
  presentationType,
  showDate = false,
  hasRule,
  onMove,
  index,
  onRemove,
  isLocked,
  orientation = 0,
  onEditStreamItem,
  streamItems = [],
}) => {
  const isStream = streamItems && streamItems.length > 0;

  return (
    <div className={cx(styles.listItem_image)}>
      {isLazy && (
        <div className="top-left">
          <Tooltip align="right" label="Dieses Element wurde extern eingebunden und wird erst durch speichern des Streams aktiviert." >
            <button className="sb-tooltip-trigger" type="button">
              <IncompleteWarning size={32} />
            </button>
          </Tooltip>
        </div>
      )}
      <ElementItem
        type={itemType}
        url={itemURL}
        items={streamItems}
        orientation={orientation}
      />
      <div className={cx(styles.topRightButton)}>
        {isStream && (
          <Button
            renderIcon={(props) => <FlowConnection size={16} {...props}/>}
            hasIconOnly
            kind="secondary"
            size="md"
            iconDescription="Element ist ein Stream"
            tooltipPosition="bottom"
            tooltipAlignment="center"
            onClick={onEditStreamItem}
          />
        )}
        {showDate && (
          <Button
            renderIcon={(props) => <Calendar size={16} {...props}/>}
            hasIconOnly
            kind="secondary"
            size="md"
            iconDescription="Element hat einen festen Zeitraum"
            tooltipPosition="bottom"
            tooltipAlignment="center"
            onClick={onEditStreamItem}
          />
        )}
        {presentationType === 2 && (
          <Button
            renderIcon={(props) => <EventSchedule size={16} {...props}/>}
            hasIconOnly
            kind="secondary"
            size="md"
            iconDescription="Element wird zu einer bestimmten Zeit angezeigt"
            tooltipPosition="bottom"
            tooltipAlignment="center"
            onClick={onEditStreamItem}
          />
        )}
        {presentationType === 1 && (
          <Button
            renderIcon={(props) => <Migrate size={16} {...props}/>}
            hasIconOnly
            kind="secondary"
            size="md"
            iconDescription={`Element nach jedem Element Xten mal angezeigt`}
            tooltipPosition="bottom"
            tooltipAlignment="center"
            onClick={onEditStreamItem}
          />
        )}
        {hasRule && (
          <Button
            renderIcon={(props) => <Policy size={16} {...props}/>}
            hasIconOnly
            size="md"
            iconDescription="Dieses Element wird nur nach einer bestimmten Regel angezeigt"
            tooltipPosition="bottom"
            tooltipAlignment="center"
          />
        )}
        {isLocked ? (
          <Button
            renderIcon={(props) => <Edit size={16} {...props}/>}
            disabled
            hasIconOnly
            size="md"
            iconDescription="Dieses Element kann nicht angepasst werden"
            tooltipPosition="bottom"
            tooltipAlignment="center"
          />
        ) : (
          <Button
            renderIcon={(props) => <Edit size={16} {...props}/>}
            hasIconOnly
            size="md"
            iconDescription="Anpassen / bearbeiten"
            tooltipPosition="bottom"
            tooltipAlignment="center"
            onClick={onEditStreamItem}
          />
        )}
        {isLocked ? (
          <Button
            renderIcon={(props) => <Locked size={16} {...props} />}
            disabled
            hasIconOnly
            size="md"
            iconDescription="Dieses Element kann nicht gelöscht werden!"
            tooltipPosition="bottom"
            tooltipAlignment="center"
          />
        ) : (
          <Button
            renderIcon={(props) => <TrashCan size={16} {...props}/>}
            hasIconOnly
            size="md"
            iconDescription="Entfernen"
            tooltipPosition="bottom"
            tooltipAlignment="center"
            onClick={() => onRemove(index)}
          />
        )}
      </div>

      <div className={cx(styles.buttonGroup)}>
        <Button
          renderIcon={DownToBottom}
          hasIconOnly
          kind="secondary"
          size="sm"
          iconDescription="Nach unten"
          tooltipPosition="right"
          tooltipAlignment="center"
          onClick={() => onMove('bottom', index)}
        />
        <Button
          renderIcon={ChevronDown}
          hasIconOnly
          kind="secondary"
          size="sm"
          iconDescription="Ein nach unten"
          tooltipPosition="right"
          tooltipAlignment="center"
          onClick={() => onMove('down', index)}
        /><Button
          renderIcon={ChevronUp}
          hasIconOnly
          kind="secondary"
          size="sm"
          iconDescription="Ein nach oben"
          tooltipPosition="right"
          tooltipAlignment="center"
          onClick={() => onMove('up', index)}
        />
        <Button
          renderIcon={UpToTop}
          hasIconOnly
          kind="secondary"
          size="sm"
          iconDescription="Nach oben"
          tooltipPosition="right"
          tooltipAlignment="center"
          onClick={() => onMove('top', index)}
        />
      </div>
    </div>
  );
};

export default StreamList;
